/**
 * Public-side AuthProvider — for members (dental practitioners / students),
 * not admins. Separate context from the admin app's auth.
 *
 * Responsibilities:
 *  - Track Firebase Auth state (user, emailVerified, profile snapshot)
 *  - On every sign-in / verify event, upsert the `members/{uid}` doc so its
 *    `emailVerified` flag stays in sync with the token
 *  - After verification, call the `linkMemberInscriptions` Cloud Function to
 *    retro-attach any guest inscriptions that match this email
 *  - Expose signIn / signUp / sendVerification / signOut helpers to pages
 */

import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  updateProfile,
  type User,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  getFirebaseClients,
  getMember,
  requestEmailVerification,
  upsertMember,
  type Member,
} from '@mtc/shared';

type AuthContextValue = {
  user: User | null;
  member: Member | null;
  /** True until the first auth state resolves. */
  isLoading: boolean;
  /** Convenience: true iff user signed in AND email verified. */
  isVerified: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName?: string) => Promise<void>;
  sendVerification: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  /** Force-refresh the auth token (after the user clicks the verification link). */
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Result of the most-recent link call, surfaced for UI confirmation. */
  lastLinkedCount: number | null;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [member, setMember] = useState<Member | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastLinkedCount, setLastLinkedCount] = useState<number | null>(null);
  const { auth, functions } = getFirebaseClients();

  // We only want to call linkMemberInscriptions once per (uid, verified=true)
  // transition. Track the last UID we linked for to avoid duplicate calls
  // on every onAuthStateChanged fire.
  const linkedForUid = useRef<string | null>(null);

  const refreshMemberDoc = useCallback(async (u: User) => {
    const doc = await getMember(u.uid);
    setMember(doc);
  }, []);

  const linkInscriptions = useCallback(
    async (u: User) => {
      if (!u.emailVerified) return;
      if (linkedForUid.current === u.uid) return;
      linkedForUid.current = u.uid;
      try {
        const call = httpsCallable<unknown, { linked: number }>(
          functions,
          'linkMemberInscriptions',
        );
        const res = await call({});
        setLastLinkedCount(res.data?.linked ?? 0);
        await refreshMemberDoc(u);
      } catch (err) {
        // Non-fatal — surface only in dev console.
        // eslint-disable-next-line no-console
        console.warn('linkMemberInscriptions failed', err);
        // Reset so a manual refresh() retries.
        linkedForUid.current = null;
      }
    },
    [functions, refreshMemberDoc],
  );

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setIsLoading(true);
      if (!u) {
        setUser(null);
        setMember(null);
        linkedForUid.current = null;
        setIsLoading(false);
        return;
      }
      setUser(u);
      // Refresh the token so emailVerified is current (Firebase doesn't push
      // the change automatically when the user clicks the verification link).
      try {
        await u.getIdToken(true);
      } catch {
        // ignore
      }
      try {
        // Mirror the auth-side flags into the member doc on every sign-in.
        await upsertMember({
          uid: u.uid,
          email: u.email ?? '',
          emailVerified: u.emailVerified,
          displayName: u.displayName ?? undefined,
        });
        await refreshMemberDoc(u);
        if (u.emailVerified) await linkInscriptions(u);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Member sync failed', err);
      } finally {
        setIsLoading(false);
      }
    });
  }, [auth, refreshMemberDoc, linkInscriptions]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      member,
      isLoading,
      isVerified: !!user && user.emailVerified,
      lastLinkedCount,
      signIn: async (email, password) => {
        await signInWithEmailAndPassword(auth, email.trim(), password);
      },
      signUp: async (email, password, displayName) => {
        const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
        if (displayName) {
          await updateProfile(cred.user, { displayName });
        }
        // The branded verification email is sent by the `onMemberWrite` Cloud
        // Function (via Resend) when the member doc is created on the
        // auth-state change that follows — not via Firebase's default mailer,
        // which lands in spam.
      },
      sendVerification: async () => {
        // Re-send the branded verification email by bumping `verifyRequestedAt`
        // on the member doc; the `onMemberWrite` function picks it up.
        if (auth.currentUser) await requestEmailVerification(auth.currentUser.uid);
      },
      resetPassword: async (email) => {
        await sendPasswordResetEmail(auth, email.trim());
      },
      refresh: async () => {
        if (!auth.currentUser) return;
        await auth.currentUser.reload();
        await auth.currentUser.getIdToken(true);
        setUser(auth.currentUser);
        if (auth.currentUser.emailVerified) await linkInscriptions(auth.currentUser);
        else await refreshMemberDoc(auth.currentUser);
      },
      signOut: async () => {
        linkedForUid.current = null;
        setLastLinkedCount(null);
        await fbSignOut(auth);
      },
    }),
    [user, member, isLoading, lastLinkedCount, auth, linkInscriptions, refreshMemberDoc],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
